import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";

const KEYS = [["flat_fee", "Flat fee ($)"], ["included_miles", "Included miles"], ["per_mile", "Per mile ($)"], ["per_minute", "Per minute ($)"]];
const toForm = (d) => ({ premium_from_first_mile: d.premium_from_first_mile, ...Object.fromEntries(["premium", "shared"].flatMap((c) => KEYS.map(([k]) => [`${c}.${k}`, String(d[c][k])]))) });

export const VehicleClassSettings = () => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get("/admin/vehicle-classes").then(({ data }) => alive && setForm((f) => f || toForm(data)));
    return () => { alive = false; };
  }, []);
  if (!form) return null;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const cls = (c) => Object.fromEntries(KEYS.map(([k]) => [k, Number(form[`${c}.${k}`]) || 0]));
    try {
      setForm(toForm((await api.put("/admin/vehicle-classes", { premium: cls("premium"), shared: cls("shared"), premium_from_first_mile: form.premium_from_first_mile })).data));
      toast.success("Vehicle class rates saved");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="lux-card mt-6 space-y-6 p-6" data-testid="vehicle-class-form">
      <p className="lux-eyebrow">Premium & Shared rates (Standard uses the fields above)</p>
      <div className="grid gap-6 md:grid-cols-2">
        {["premium", "shared"].map((c) => (
          <div key={c} className="space-y-4">
            <p className="font-serif text-lg capitalize text-amber-100">{c}</p>
            {KEYS.map(([k, l]) => (
              <label key={k} className="block"><span className="lux-eyebrow mb-2 block">{l}</span>
                <input type="number" min="0" step="0.01" data-testid={`class-${c}-${k}-input`} className="lux-input" value={form[`${c}.${k}`]}
                  onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, [`${c}.${k}`]: v })); }} />
              </label>
            ))}
          </div>
        ))}
      </div>
      <label className="flex items-center gap-3 text-sm text-zinc-300">
        <Switch data-testid="premium-from-first-mile-toggle" checked={form.premium_from_first_mile} onCheckedChange={(v) => setForm((f) => ({ ...f, premium_from_first_mile: v }))} />
        Premium per-mile from first mile (per-mile × all miles, no flat fee)
      </label>
      <button type="submit" disabled={busy} data-testid="vehicle-class-save-button" className="btn-gold">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save class rates</button>
    </form>
  );
};

export const AISettings = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/ai-settings").then(({ data }) => setS(data)); }, []);
  if (!s) return null;
  const pick = async (model) => {
    try { setS((await api.put("/admin/ai-settings", { model })).data); toast.success("AI model updated for new conversations"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="lux-card max-w-xl space-y-4 p-6" data-testid="ai-settings">
      <p className="lux-eyebrow">Assistant model (Emergent Universal Key)</p>
      {s.options.map((o) => (
        <button key={o.value} data-testid={`ai-model-${o.value}`} onClick={() => pick(o.value)}
          className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors ${s.model === o.value ? "border-amber-400 bg-amber-400/10" : "border-white/10 hover:border-amber-500/40"}`}>
          <span><span className="block text-amber-100">{o.label}</span><span className="font-mono text-xs text-zinc-500">{o.model_id}</span></span>
          {s.model === o.value && <span data-testid="ai-model-active" className="text-xs text-amber-300">Active</span>}
        </button>
      ))}
    </div>
  );
};
